/**
 * TrialSidebarBadge — Sidebar trial status indicator (N5 enhanced).
 *
 * Three visual phases based on days remaining:
 *   Calm (14–8d): teal icon, default border
 *   Attention (7–4d): amber icon + border
 *   Urgent (3–0d): red pulsing icon + border
 *
 * Entire badge is clickable → navigates to settings?tab=billing.
 * Active paid subscription: renders nothing.
 */

import { useMemo } from "react"
import { useNavigate } from "react-router"
import { Sparkles, ArrowRight } from "lucide-react"
import { cn } from "@/lib/utils"
import { usePlanContext } from "@/lib/usePlanContext"

export function TrialSidebarBadge() {
  const { plan, status, isTrialing, trialEndsAt, degraded } = usePlanContext()
  const navigate = useNavigate()

  const daysLeft = useMemo(() => {
    if (!trialEndsAt) return null
    const end = new Date(trialEndsAt)
    const now = new Date()
    const diffMs = end.getTime() - now.getTime()
    if (diffMs <= 0) return 0
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24))
  }, [trialEndsAt])

  const phase = useMemo(() => {
    if (daysLeft === null) return "none" as const
    if (daysLeft <= 3) return "urgent" as const
    if (daysLeft <= 7) return "attention" as const
    return "calm" as const
  }, [daysLeft])

  // Active paid subscription — render nothing
  if (status === "active" && !isTrialing) return null

  // Trial expired — only when the backend explicitly says so.
  // Previously `(degraded && plan === "host")` was too broad: it triggered when
  // the property-settings API failed to resolve the subscription (silent failure)
  // even though the user had an active trial. The billing fallback in usePlanContext
  // now catches that, so we only show "Trial ended" for explicit expiry.
  const isExpired =
    status === "trial_expired" ||
    status === "past_due" ||
    (degraded && plan === "host" && status === "no_subscription")

  if (isExpired) {
    return (
      <button
        type="button"
        onClick={() => navigate("settings?tab=billing")}
        className="bg-card/60 backdrop-blur-sm border border-border/40 rounded-lg px-3 py-2.5 mx-3 cursor-pointer hover:bg-card/80 transition-colors text-left w-auto block"
      >
        <p className="text-xs font-medium text-muted-foreground">Trial ended</p>
        <span className="text-xs text-primary font-medium inline-flex items-center gap-1 mt-1">
          Choose a plan
          <ArrowRight className="h-3 w-3" />
        </span>
      </button>
    )
  }

  // Active trial
  if (isTrialing && daysLeft !== null) {
    const progress = ((14 - daysLeft) / 14) * 100

    let label: string
    if (daysLeft === 0) label = "Trial ends today"
    else if (daysLeft === 1) label = "Trial ends tomorrow"
    else label = `Trial \u00b7 ${daysLeft} days left`

    return (
      <button
        type="button"
        onClick={() => navigate("settings?tab=billing")}
        className={cn(
          "bg-card/60 backdrop-blur-sm rounded-lg px-3 py-2.5 mx-3 cursor-pointer hover:bg-card/80 transition-colors text-left w-auto block border",
          phase === "calm" && "border-border/40",
          phase === "attention" && "border-amber-300/60",
          phase === "urgent" && "border-red-300/60"
        )}
      >
        <div className="flex items-center gap-1.5">
          <Sparkles
            className={cn(
              "h-3.5 w-3.5",
              phase === "calm" && "text-teal-600",
              phase === "attention" && "text-amber-500",
              phase === "urgent" && "text-red-500 animate-pulse [animation-duration:3s]"
            )}
          />
          <span className={cn(
            "text-xs font-semibold tracking-wide",
            phase === "calm" && "text-foreground",
            phase === "attention" && "text-amber-700 dark:text-amber-400",
            phase === "urgent" && "text-red-700 dark:text-red-400"
          )}>
            {label}
          </span>
        </div>
        <div className="h-0.5 w-full bg-muted rounded-full overflow-hidden mt-2">
          <div
            className={cn(
              "h-full rounded-full transition-all duration-500",
              phase === "calm" && "bg-teal-600",
              phase === "attention" && "bg-amber-500",
              phase === "urgent" && "bg-red-500"
            )}
            style={{ width: `${Math.min(progress, 100)}%` }}
          />
        </div>
        {(phase === "attention" || phase === "urgent") && (
          <span className="text-[10px] text-primary font-medium inline-flex items-center gap-0.5 mt-1.5">
            Subscribe now
            <ArrowRight className="h-2.5 w-2.5" />
          </span>
        )}
      </button>
    )
  }

  return null
}
